import React from "react";
import styles from "./css/HomePage.module.css";
import { buildSiteUrl } from "../config/runtime";
import { useSiteContent } from "../hooks/useSiteContent";
import MetaManager from "./MetaManager";

function isExternal(url) {
  return /^https?:\/\//i.test(String(url || "").trim());
}

function LinkButton({ href, children, secondary = false }) {
  const className = secondary ? styles.secondaryButton : styles.primaryButton;
  if (isExternal(href)) {
    return (
      <a className={className} href={href} target="_blank" rel="noreferrer">
        {children}
      </a>
    );
  }

  return (
    <a className={className} href={href || "/"}>
      {children}
    </a>
  );
}

export default function HomePage() {
  const { content, loading } = useSiteContent();
  const home = content?.home || {};
  const hero = home.hero || {};
  const features = Array.isArray(home.features) ? home.features : [];
  const useCases = Array.isArray(home.useCases) ? home.useCases : [];
  const steps = Array.isArray(home.steps) ? home.steps : [];
  const faq = Array.isArray(home.faq) ? home.faq : [];
  const contacts = home.contacts || {};
  const cta = home.cta || {};

  return (
    <div className={styles.page}>
      <MetaManager
        seo={content?.seo?.home}
        fallbackTitle="Сверхум — ИИ-помощник педагога"
        fallbackDescription="Сверхум помогает педагогам готовить материалы, сценарии, аттестационные тексты, контент для госпабликов и визуалы быстрее."
        fallbackPath="/"
      />

      <div className={styles.shell}>
        <header className={styles.topbar}>
          <a className={styles.brand} href="/">
            <span className={styles.brandMark} />
            <span>Сверхум</span>
          </a>
          <nav className={styles.topnav}>
            <a href="#cases">Сценарии</a>
            <a href="#faq">FAQ</a>
            <a href={buildSiteUrl("/payment")}>Тарифы</a>
          </nav>
        </header>

        <section className={styles.hero}>
          <div className={styles.heroText}>
            <p className={styles.eyebrow}>{hero.eyebrow}</p>
            <h1>{hero.title}</h1>
            <p className={styles.subtitle}>{hero.subtitle}</p>
            <div className={styles.heroActions}>
              <LinkButton href={hero.primaryUrl}>{hero.primaryLabel}</LinkButton>
              <LinkButton href={hero.secondaryUrl} secondary>
                {hero.secondaryLabel}
              </LinkButton>
            </div>
            <p className={styles.footnote}>{hero.footnote}</p>
          </div>

          <div className={styles.heroPanel}>
            <div className={styles.badgeList}>
              {(hero.badges || []).map((badge) => (
                <span key={badge} className={styles.badge}>
                  {badge}
                </span>
              ))}
            </div>
            <div className={styles.heroCard}>
              <div className={styles.heroCardLabel}>Подходит для реальных задач</div>
              <div className={styles.heroCardValue}>От идеи до готового материала</div>
              <p>
                Сверхум помогает не просто генерировать текст, а собирать рабочие сценарии, структуры,
                методические формулировки и визуалы под образовательную задачу.
              </p>
            </div>
            <div className={styles.heroStats}>
              <div>
                <strong>1 бот</strong>
                <span>для методических, текстовых и визуальных задач</span>
              </div>
              <div>
                <strong>3 шага</strong>
                <span>от запроса до готового черновика</span>
              </div>
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionHeading}>
            <p className={styles.sectionKicker}>Что умеет бот</p>
            <h2>Рабочие блоки, которые закрывают повседневную нагрузку педагога</h2>
          </div>
          <div className={styles.featureGrid}>
            {features.map((item) => (
              <article key={item.title} className={styles.featureCard}>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className={styles.section} id="cases">
          <div className={styles.sectionHeading}>
            <p className={styles.sectionKicker}>Сценарии использования</p>
            <h2>Сверхум встроен в типовые образовательные процессы, а не живёт отдельно от них</h2>
          </div>
          <div className={styles.caseGrid}>
            {useCases.map((item) => (
              <article key={item.title} className={styles.caseCard}>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionHeading}>
            <p className={styles.sectionKicker}>Как начать</p>
            <h2>Простой путь без долгого онбординга</h2>
          </div>
          <div className={styles.stepGrid}>
            {steps.map((item, index) => (
              <article key={item.title} className={styles.stepCard}>
                <span className={styles.stepNumber}>0{index + 1}</span>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className={styles.ctaSection}>
          <div>
            <p className={styles.sectionKicker}>Подключение</p>
            <h2>{cta.title}</h2>
            <p>{cta.text}</p>
          </div>
          <div className={styles.ctaActions}>
            <LinkButton href={cta.primaryUrl}>{cta.primaryLabel}</LinkButton>
            <LinkButton href={cta.secondaryUrl} secondary>
              {cta.secondaryLabel}
            </LinkButton>
          </div>
        </section>

        <section className={styles.bottomGrid}>
          <div className={styles.contactCard}>
            <p className={styles.sectionKicker}>Контакты</p>
            <h2>{contacts.title}</h2>
            <p>{contacts.text}</p>
            <div className={styles.contactList}>
              {(contacts.items || []).map((item) => (
                <div key={`${item.label}-${item.value}`} className={styles.contactItem}>
                  <span>{item.label}</span>
                  {item.url ? (
                    <a href={item.url} target={isExternal(item.url) ? "_blank" : undefined} rel="noreferrer">
                      {item.value || item.url}
                    </a>
                  ) : (
                    <strong>{item.value}</strong>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className={styles.faqCard} id="faq">
            <p className={styles.sectionKicker}>FAQ</p>
            <h2>Частые вопросы</h2>
            <div className={styles.faqList}>
              {faq.map((item) => (
                <details key={item.question} className={styles.faqItem}>
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <footer className={styles.footer}>
          <span>Сверхум — ИИ-помощник педагога</span>
          {/* <span>{loading ? "Обновляем контент…" : "Контент синхронизирован с админкой"}</span> */}
        </footer>
      </div>
    </div>
  );
}
